/**
 * Privacy Preferences Policy Control (PPPC) types and constants.
 *
 * PPPC uses Apple's TCC (Transparency, Consent, and Control) configuration
 * profile payload (`com.apple.TCC.configuration-profile-policy`) to pre-approve
 * or deny a macOS app's access to privacy-sensitive services without prompting
 * the end user.
 */

export const PPPC_TCC_IDENTIFIER = "com.apple.TCC.configuration-profile-policy";

export type PppcServiceKey =
  // Media & Input
  | "Camera"
  | "Microphone"
  | "Photos"
  | "MediaLibrary"
  | "SpeechRecognition"
  // System Access
  | "Accessibility"
  | "SystemPolicyAllFiles"
  | "SystemPolicySysAdminFiles"
  | "SystemPolicyDesktopFolder"
  | "SystemPolicyDocumentsFolder"
  | "SystemPolicyDownloadsFolder"
  | "SystemPolicyNetworkVolumes"
  | "SystemPolicyRemovableVolumes"
  | "SystemPolicyAppBundles"
  | "SystemPolicyAppData"
  // Events & Automation
  | "AppleEvents"
  | "PostEvent"
  | "ListenEvent"
  | "ScreenCapture"
  // Other
  | "AddressBook"
  | "Calendar"
  | "Reminders"
  | "FileProviderPresence"
  | "BluetoothAlways";

export type PppcChoice = "unset" | "allow" | "deny";

export interface PppcServiceDef {
  key: PppcServiceKey;
  label: string;
  description: string;
  /** Some services can only be denied by MDM, never granted (Apple restriction). */
  denyOnly?: boolean;
}

export interface PppcServiceGroup {
  groupLabel: string;
  services: PppcServiceDef[];
}

export const PPPC_SERVICE_GROUPS: PppcServiceGroup[] = [
  {
    groupLabel: "Media & Input",
    services: [
      { key: "Camera", label: "Camera", description: "Access to system camera", denyOnly: true },
      { key: "Microphone", label: "Microphone", description: "Access to system microphone", denyOnly: true },
      { key: "Photos", label: "Photos", description: "Access to Photos library" },
      { key: "MediaLibrary", label: "Media Library", description: "Access to Apple Music and media library" },
      { key: "SpeechRecognition", label: "Speech Recognition", description: "Use system Speech Recognition" },
    ],
  },
  {
    groupLabel: "System Access",
    services: [
      { key: "Accessibility", label: "Accessibility", description: "Control the device via Accessibility" },
      { key: "SystemPolicyAllFiles", label: "Full Disk Access", description: "Full Disk Access (all protected files)" },
      { key: "SystemPolicySysAdminFiles", label: "System Admin Files", description: "System administration files" },
      { key: "SystemPolicyDesktopFolder", label: "Desktop Folder", description: "Desktop folder access" },
      { key: "SystemPolicyDocumentsFolder", label: "Documents Folder", description: "Documents folder access" },
      { key: "SystemPolicyDownloadsFolder", label: "Downloads Folder", description: "Downloads folder access" },
      { key: "SystemPolicyNetworkVolumes", label: "Network Volumes", description: "Network volume access" },
      { key: "SystemPolicyRemovableVolumes", label: "Removable Volumes", description: "Removable volume access" },
      { key: "SystemPolicyAppBundles", label: "App Bundles", description: "Update or delete other apps (macOS 13+)" },
      { key: "SystemPolicyAppData", label: "App Data", description: "Access data of other apps" },
    ],
  },
  {
    groupLabel: "Events & Automation",
    services: [
      { key: "AppleEvents", label: "Apple Events", description: "Send Apple Events to other processes" },
      { key: "PostEvent", label: "Post Event", description: "Send CGEvents to the system event stream" },
      { key: "ListenEvent", label: "Listen Event", description: "Listen to CGEvents/HID events", denyOnly: true },
      { key: "ScreenCapture", label: "Screen Capture", description: "Capture display contents", denyOnly: true },
    ],
  },
  {
    groupLabel: "Other",
    services: [
      { key: "AddressBook", label: "Contacts", description: "Access Contacts" },
      { key: "Calendar", label: "Calendar", description: "Access Calendar" },
      { key: "Reminders", label: "Reminders", description: "Access Reminders" },
      { key: "FileProviderPresence", label: "File Provider", description: "File Provider presence detection" },
      { key: "BluetoothAlways", label: "Bluetooth", description: "Access Bluetooth devices" },
    ],
  },
];

export interface PppcServiceEntry {
  Identifier: string;
  IdentifierType: "bundleID" | "path";
  CodeRequirement: string;
  Allowed: boolean;
}

export type PppcServicesDict = Partial<Record<PppcServiceKey, PppcServiceEntry[]>>;

export type PppcChoicesMap = Partial<Record<PppcServiceKey, PppcChoice>> &
  Record<string, PppcChoice | undefined>;

/**
 * Lookup a known PPPC service definition by its TCC key, or synthesize a
 * minimal definition for custom/unknown keys (forward-compatibility with new
 * macOS releases). The synthesized def assumes the service is allow-capable.
 */
export const getPppcServiceDef = (key: string): PppcServiceDef => {
  for (const group of PPPC_SERVICE_GROUPS) {
    const found = group.services.find((s) => s.key === key);
    if (found) return found;
  }
  return {
    key: key as PppcServiceKey,
    label: key,
    description: "Custom TCC service",
  };
};

/**
 * Resolve the group label a service belongs to (or "Custom" for unknown keys).
 */
export const getPppcServiceGroupLabel = (key: string): string => {
  for (const group of PPPC_SERVICE_GROUPS) {
    if (group.services.some((s) => s.key === key)) return group.groupLabel;
  }
  return "Custom";
};

/**
 * True if `key` is one of the built-in TCC services we ship in
 * PPPC_SERVICE_GROUPS. Anything else is a custom/unknown key (forward-
 * compatibility with new macOS releases or admin-defined entries).
 */
export const isCustomPppcServiceKey = (key: string): boolean => {
  for (const group of PPPC_SERVICE_GROUPS) {
    if (group.services.some((s) => s.key === key)) return false;
  }
  return true;
};

export type PppcIdentifierType = "bundleID" | "path";

export interface PppcPolicySnapshot {
  policyId: string;
  codeRequirement: string;
  identifier: string;
  identifierType: PppcIdentifierType;
  appName?: string;
  choices: PppcChoicesMap;
  /** Backend timestamp string */
  updatedAt?: string;
  /** Original policy name (kept so the library can surface it). */
  policyName?: string;
}

export interface SavePppcArgs {
  existingPolicyId?: string;
  appName: string;
  /** App identifier value — either a bundle ID or a path depending on identifierType. */
  bundleId: string;
  identifierType?: PppcIdentifierType;
  codeRequirement: string;
  choices: PppcChoicesMap;
  /** Tag ids to assign after policy save. Required for external targets, optional for catalog. */
  tagIds?: string[];
  /** Tag ids previously assigned (used to unassign removed tags in edit mode). */
  previousTagIds?: string[];
  /** Optional tenant scope for the underlying policy. Empty/undefined falls
   *  back to no scope. */
  tenantIds?: string[];
  allTenants?: boolean;
}

/**
 * Naming convention for PPPC policies. For path-based entries we surface the
 * basename so the policy list stays scannable.
 */
export const buildPppcPolicyName = (
  appName: string,
  identifier: string,
  identifierType: PppcIdentifierType = "bundleID",
) => {
  if (identifierType === "path") {
    const parts = identifier.split("/").filter(Boolean);
    const basename = parts[parts.length - 1] || identifier;
    return `PPPC — ${appName} (${basename})`;
  }
  return `PPPC — ${appName} (${identifier})`;
};
